import { logout } from 'actions/login';
import { updatePaymentProfileDetails } from 'actions/profileNew';
import { Button, Input, Modal } from 'antd';
import { Heading, SubHeading } from 'components/Typography';
import { getAddressFromPin } from 'pages/profile/postalApi';
import { validateEmail } from 'pages/studentAulasWallet/studentKycData';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

// basicprofile.currentAddress comes back as a JSON string, but a few callers already hold the
// parsed object -- accept either rather than making every call site remember which it has.
export const parseAddress = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value) || {};
    } catch (e) {
        return {};
    }
};

// The gateway wants the billing address as a single string rather than as parts: house no and
// street, locality, city/district, state, country, pincode. Composing it here keeps every payment
// entry point sending one format. Repeated parts are dropped because the pincode lookup returns
// one value for both city and district, which would otherwise read as "Bengaluru, Bengaluru".
export const formatBillingAddress = (address) => {
    const parsed = parseAddress(address);
    const parts = [parsed?.h_no, parsed?.locality, parsed?.city, parsed?.district, parsed?.state, parsed?.country, parsed?.pincode]
        .map((part) => (part ?? '').toString().trim())
        .filter(Boolean);
    return parts
        .filter((part, index) => parts.findIndex((other) => other.toLowerCase() === part.toLowerCase()) === index)
        .join(', ');
};

// The gateway rejects an order without an email and, once the backend's placeholder address is
// removed, one without a billing address. Neither is recoverable at redirect time, so a payment is
// gated here on whichever of the two the profile is missing.
export const getMissingPaymentDetails = (userDetails) => {
    const address = parseAddress(userDetails?.basicprofile?.currentAddress);
    const email = userDetails?.email || '';
    const needsEmail = !email;
    const needsAddress = !(address?.h_no || '').trim() || !(address?.pincode || '').trim();
    // An unloaded profile looks identical to one missing both fields, so prompting on it would ask
    // a user for details they already have -- and re-sending their own email reads as a change,
    // which logs them out. The mobile components render before their parent's fetch resolves, so
    // treat "not loaded yet" as "nothing to ask for" and let the profile arrive first.
    const isLoaded = !!userDetails?.id;
    return { email, address, needsEmail, needsAddress, isMissing: isLoaded && (needsEmail || needsAddress) };
};

const inputStyle = {
    width: '100%',
    border: '#E6E6E6 solid 1px',
    borderRadius: '5px',
    height: '40px',
};

const SAVE_TIMEOUT_MS = 20000;

// show.message is a plain string most of the time, but an object of {title, description} when the
// backend wants the message rendered as a popup.
const showMessage = (show) => (
    typeof show?.message === 'string'
        ? show.message
        : show?.message?.description || show?.message?.title || ''
);

// Every field the modal shows is mandatory -- it only renders what the profile is missing, and
// PROCEED stays disabled until all of it is filled -- so the marker is unconditional.
const FieldLabel = ({ children }) => (
    <div style={{ color: '#636363' }}>
        {children}<span style={{ color: '#FF414D' }}> *</span>
    </div>
);

function PaymentDetailsModal({ visible, onCancel, onProceed, userDetails, updatePaymentProfileDetails, logout }) {
    const { email: savedEmail, address: savedAddress, needsEmail, needsAddress } = getMissingPaymentDetails(userDetails);
    const history = useHistory();
    const [email, setEmail] = useState('');
    const [addressLine, setAddressLine] = useState('');
    const [pincode, setPincode] = useState('');
    const [region, setRegion] = useState({ city: '', state: '', country: 'India' });
    const [pincodeLoading, setPincodeLoading] = useState(false);
    // idle | ok | notFound | unavailable -- see the lookup effect for how the last two differ.
    const [pincodeStatus, setPincodeStatus] = useState('idle');
    const [saving, setSaving] = useState(false);
    // Server-side rejection of the save, e.g. an email already linked to another account.
    const [saveError, setSaveError] = useState('');
    // The email path logs out, which unmounts this modal mid-request.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    useEffect(() => {
        if (!visible) return;
        setEmail(savedEmail || '');
        setAddressLine(savedAddress?.h_no || '');
        setPincode(savedAddress?.pincode || '');
        setRegion({
            city: savedAddress?.city || '',
            state: savedAddress?.state || '',
            country: savedAddress?.country || 'India',
        });
        setPincodeLoading(false);
        setPincodeStatus('idle');
        setSaving(false);
        setSaveError('');
    }, [visible]);

    // City and state are derived from the pincode the way the profile address form does it, so the
    // address that reaches the gateway can never disagree with its own pincode.
    //
    // The lookup fails in two ways that need telling apart. A pincode that does not exist comes
    // back as a 200 whose PostOffice list is empty, which reaches the callback as blank parts --
    // the user mistyped, so that blocks. A transport failure never reaches the callback at all,
    // because getAddressFromPin swallows it; that is the postal service being down, not the user's
    // mistake, so it warns without blocking rather than making an outage un-payable.
    useEffect(() => {
        if (!visible || !needsAddress) return;
        if (pincode?.length !== 6) {
            setPincodeLoading(false);
            setPincodeStatus('idle');
            setRegion((prev) => ({ ...prev, city: '', state: '' }));
            return;
        }
        let cancelled = false;
        let answered = false;
        setPincodeLoading(true);
        setPincodeStatus('idle');
        getAddressFromPin(pincode, (city, state, country) => {
            if (cancelled) return;
            answered = true;
            if (!city && !state) {
                setRegion({ city: '', state: '', country: 'India' });
                setPincodeStatus('notFound');
                return;
            }
            setRegion({ city: city || '', state: state || '', country: country || 'India' });
            setPincodeStatus('ok');
        })
            .catch(() => { })
            // Settles either way, so this is also what clears the loader -- doing it in the
            // callback would leave PROCEED disabled for good on a failed lookup.
            .then(() => {
                if (cancelled) return;
                setPincodeLoading(false);
                if (!answered) {
                    setRegion((prev) => ({ ...prev, city: '', state: '' }));
                    setPincodeStatus('unavailable');
                }
            });
        // A pincode edited mid-flight abandons the in-flight lookup, so a slow earlier response
        // cannot land on top of a newer one.
        return () => { cancelled = true; };
    }, [visible, pincode]);

    // validateEmail returns true when the address is malformed, so a valid one is non-empty and
    // fails that check.
    const emailValid = !!email?.trim() && !validateEmail(email);
    const emailInvalid = needsEmail && !emailValid;
    // 'unavailable' deliberately does not block: the pincode may well be correct and the payment
    // should not depend on a third-party postal API being up.
    const addressInvalid = needsAddress && (!addressLine?.trim() || pincode?.length !== 6 || pincodeLoading || pincodeStatus === 'notFound');

    // updatePaymentProfileDetails only ever reports success, through its third argument. It has no
    // failure signal to hook: the underlying uploadAndPost reports an HTTP error by calling back
    // with percent -1 and no response, which matches neither of saveProfileDetails' two conditions,
    // so a failed save is indistinguishable from one still in flight. The timer is what guarantees
    // this settles -- without it a dropped request leaves the gateway window parked on its loading
    // page indefinitely with no way back.
    const savePaymentDetails = (payload) => new Promise((resolve, reject) => {
        let settled = false;
        let timer = null;
        const settle = (finish, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            finish(value);
        };
        timer = setTimeout(
            () => settle(reject, new Error('Saving your details timed out. Please try again.')),
            SAVE_TIMEOUT_MS,
        );
        updatePaymentProfileDetails(
            payload,
            // Only percent -1 means the request failed, and it is the sole signal a transport error
            // produces -- so this is what stops the spinner rather than the timeout. Percent 100 is
            // ignored on purpose: onUploadProgress reports it the moment the upload completes, well
            // before the server replies, so treating it as terminal would reject a save that then
            // succeeds. The deferral covers a 200 carrying show.type "error", where uploadAndPost
            // emits -1 immediately before delivering the response below; it lets that more specific
            // server message settle first.
            (percent, errorResponse) => {
                if (percent !== -1) return;
                const reason = showMessage(errorResponse?.data?.show);
                setTimeout(
                    () => settle(reject, new Error(reason || 'Could not save your details. Please try again.')),
                    0,
                );
            },
            (response) => {
                // A rejected save arrives here looking like a success. uploadAndPost reports a 200
                // that carries show.type "error" -- an email already linked to another account, say
                // -- with callback(-1) followed by callback(100, response), and saveProfileDetails
                // reacts to the second. The show block is the only thing separating the two.
                const show = response?.data?.show;
                if (show?.type === 'error') {
                    settle(reject, new Error(showMessage(show) || 'Could not save your details. Please try again.'));
                    return;
                }
                settle(resolve, response);
            },
        );
    });

    // The backend reports a credential change by putting logout: true on an otherwise successful
    // save. It is the authority on whether the session survived, so it decides -- not our guess
    // about which box was ticked.
    const endsSession = (response) => !!response?.data?.show?.logout;

    const handleProceed = () => {
        const address = { h_no: addressLine?.trim(), pincode, ...region };
        const details = {
            email: needsEmail ? email?.trim() : savedEmail,
            address: needsAddress ? address : savedAddress,
        };
        // The modal completes the profile rather than offering to, so whatever was missing is always
        // saved -- there is no longer an opt-out for the user to leave unticked.
        const savePayload = {};
        if (needsEmail) savePayload.email = details.email;
        if (needsAddress) savePayload.address = details.address;

        // Saving the email ends the session, so this path finishes at the login screen rather than
        // at the gateway -- opening a payment window the user is about to be logged out of would
        // strand them mid-transaction. Any address ticked alongside it is saved by the same call.
        // Ticking the email box is known up front to end the session, so this path never opens a
        // gateway window it would only have to abandon.
        if (savePayload.email) {
            setSaving(true);
            setSaveError('');
            savePaymentDetails(savePayload)
                .then((response) => {
                    onCancel();
                    if (endsSession(response)) logout(history, 'profile-updated');
                })
                .catch((error) => {
                    // Stay open rather than closing on a save that did not happen -- a rejected
                    // email is fixable right here, and closing would strand the user with a toast.
                    if (!mountedRef.current) return;
                    setSaving(false);
                    setSaveError(error?.message || 'Could not save your details. Please try again.');
                });
            return;
        }

        if (!Object.keys(savePayload).length) {
            onProceed(details, null);
            return;
        }

        // Address-only: the payment waits for the save to land. onProceed still runs synchronously
        // so the gateway window opens inside this click -- the parent parks it on the loading page
        // until the promise settles, rather than opening it after the response and being blocked.
        onProceed(details, savePaymentDetails(savePayload).then((response) => {
            // Saving an address should not end the session, but if the backend says it did, that
            // wins: paying on a dead token would fail at the gateway anyway. Throwing aborts the
            // payment, which closes the window the parent opened.
            if (endsSession(response)) {
                logout(history, 'profile-updated');
                throw new Error('Session ended by profile update');
            }
        }));
    };

    const missingLabel = needsEmail && needsAddress ? 'Email ID and address' : needsEmail ? 'Email ID' : 'Address';
    const missingFields = [needsEmail && 'email', needsAddress && 'address'].filter(Boolean).join(' and ');

    return (
        <Modal
            className="modal-round-corner"
            centered
            visible={visible}
            onOk={onCancel}
            onCancel={onCancel}
            header={null}
            footer={null}
            width={600}>
            <Heading bold className='r-c-c m-t-10'>{missingLabel} required</Heading>
            <SubHeading color="#636363" className='r-c-c m-t-10' style={{ textAlign: 'center' }}>
                To continue with the payment please complete the profile ({missingFields})
            </SubHeading>
            {needsEmail && (
                <div className='m-t-20'>
                    <div className='r-jsb'>
                        <FieldLabel>Email ID</FieldLabel>
                        {validateEmail(email) ? <div style={{ color: 'red' }}>Enter valid email id</div> : <></>}
                    </div>
                    <Input
                        style={inputStyle}
                        value={email}
                        placeholder={'Enter email ID'}
                        onChange={(e) => { setEmail(e.target.value); setSaveError(''); }}
                    />
                    {/* The email is now always saved, so the session always ends -- warn up front
                        rather than only once a box has been ticked. */}
                    <div className='m-t-10' style={{ color: '#FF414D', fontSize: 12 }}>
                        Saving your email ID will log you out. You'll need to log in again.
                    </div>
                </div>
            )}
            {needsAddress && (
                <div className='m-t-20'>
                    <FieldLabel>Address</FieldLabel>
                    <Input
                        style={inputStyle}
                        value={addressLine}
                        placeholder={'Enter address'}
                        onChange={(e) => setAddressLine(e.target.value)}
                    />
                    <div className='r-jsb m-t-15'>
                        <FieldLabel>Pincode</FieldLabel>
                        {pincode && pincode.length !== 6 ? <div style={{ color: 'red' }}>Enter a 6 digit pincode</div> : <></>}
                    </div>
                    <Input
                        style={inputStyle}
                        value={pincode}
                        maxLength={6}
                        placeholder={'Enter pincode'}
                        onChange={(e) => setPincode(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                    {pincodeLoading ? (
                        <div className='m-t-5' style={{ color: '#636363', fontSize: 12 }}>Fetching location...</div>
                    ) : pincodeStatus === 'notFound' ? (
                        <div className='m-t-5' style={{ color: '#FF414D', fontSize: 12 }}>
                            We could not find this pincode. Please check and try again.
                        </div>
                    ) : pincodeStatus === 'unavailable' ? (
                        <div className='m-t-5' style={{ color: '#FFA931', fontSize: 12 }}>
                            Could not verify the pincode right now. You can continue.
                        </div>
                    ) : region?.city || region?.state ? (
                        <div className='m-t-5' style={{ color: '#636363', fontSize: 12 }}>
                            {[region?.city, region?.state, region?.country].filter(Boolean).join(', ')}
                        </div>
                    ) : <></>}
                </div>
            )}
            {saveError ? (
                <div className='m-t-20 r-c-c' style={{ color: '#FF414D', fontSize: 12, textAlign: 'center' }}>
                    {saveError}
                </div>
            ) : <></>}
            <div style={{ display: 'flex', justifyContent: 'space-evenly', marginTop: saveError ? '20px' : '40px', marginBottom: 10 }}>
                <Button
                    style={{
                        borderRadius: '19px',
                        width: '120px',
                        height: '35px',
                        border: '1px solid  #636363',
                        backgroundColor: '#FFFFFF',
                        color: '#594099',
                    }}
                    disabled={saving}
                    onClick={onCancel}>
                    CANCEL
                </Button>
                <Button
                    disabled={emailInvalid || addressInvalid || saving}
                    loading={saving}
                    style={{ borderRadius: '19px', width: '120px', height: '35px' }}
                    type='primary'
                    onClick={handleProceed}>
                    {needsEmail ? 'SAVE' : 'PROCEED'}
                </Button>
            </div>
        </Modal>
    );
}

const mapStateToProps = (state) => {
    const { userDetails } = state.profileNew;
    return { userDetails };
};

const mapDispatchToProps = (dispatch) => ({
    updatePaymentProfileDetails: (details, callback, successCallBack) => dispatch(updatePaymentProfileDetails(details, callback, successCallBack)),
    logout: (history, reason) => dispatch(logout(history, reason)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentDetailsModal);
